/**
 * Header selectors — Category, Number and Date — turning a picked value into
 * a WHERE clause, the same SQL-92-ish shape `buildWhereClause` already
 * produces for a widget's own filters and `buildConstraint` produces for a
 * click-to-filter action.
 *
 * Deliberately its own small system rather than folded into `actionRuntime`:
 * a selector has no source/target wiring to resolve (it always reaches every
 * widget on its own data source — see `DashboardSelector`'s own doc comment)
 * and its value shapes — a value range, a date range — do not fit
 * `ActionSignal`'s single `value` field.
 */

import type { DashboardSelector } from '../types/dashboardTypes';

/** One selector's current picked value(s). Absent members mean "not set yet". */
export interface SelectorValue {
  /** Category only. */
  categoryValues?: string[];
  /** Number, `mode: 'single'`. */
  numberSingle?: number | null;
  /** Number, `mode: 'range'`. Either bound alone still narrows. */
  numberMin?: number | null;
  numberMax?: number | null;
  /** Date, `mode: 'single'` — an ISO `YYYY-MM-DD` day. */
  dateSingle?: string | null;
  /** Date, `mode: 'range'`. Either bound alone still narrows. */
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** Every selector's current value, keyed by `DashboardSelector.id`. */
export type SelectorState = Record<string, SelectorValue>;

/**
 * Quotes a category value if it looks like text, leaves it bare if it looks
 * like a number.
 *
 * Safe to decide from the VALUE alone here, unlike a free-typed filter box
 * (see `toolFields.ts`'s own warning about that): a category selector's
 * options come straight from `fetchDistinctValues` on the real field, never
 * typed by hand, so if the field is genuinely numeric every one of its values
 * already looks like a number — there is no ambiguous "10" that could be
 * either.
 */
function categoryLiteral(value: string): string {
  return value.trim() !== '' && Number.isFinite(Number(value))
    ? value
    : `'${value.replace(/'/g, "''")}'`;
}

/** The day after an ISO `YYYY-MM-DD` date, for an exclusive upper bound. */
function nextDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** One selector's own contribution to a WHERE clause — `''` while unset. */
export function whereForSelector(
  selector: DashboardSelector,
  value: SelectorValue | undefined,
): string {
  if (!value || !selector.field) return '';
  const field = selector.field;

  if (selector.type === 'category') {
    const values = (value.categoryValues ?? []).filter((entry) => entry !== '');
    if (values.length === 0) return '';
    if (values.length === 1) return `${field} = ${categoryLiteral(values[0])}`;
    return `${field} IN (${values.map(categoryLiteral).join(', ')})`;
  }

  if (selector.type === 'number') {
    if (selector.mode === 'range') {
      const parts: string[] = [];
      if (value.numberMin !== null && value.numberMin !== undefined) {
        parts.push(`${field} >= ${value.numberMin}`);
      }
      if (value.numberMax !== null && value.numberMax !== undefined) {
        parts.push(`${field} <= ${value.numberMax}`);
      }
      return parts.join(' AND ');
    }
    return value.numberSingle !== null && value.numberSingle !== undefined
      ? `${field} = ${value.numberSingle}`
      : '';
  }

  // date — a day boundary either way, since the underlying column is often a
  // full timestamp: picking "22 Sep" should not miss a record stamped 14:30.
  if (selector.mode === 'range') {
    const parts: string[] = [];
    if (value.dateFrom) parts.push(`${field} >= '${value.dateFrom}'`);
    if (value.dateTo) parts.push(`${field} < '${nextDay(value.dateTo)}'`);
    return parts.join(' AND ');
  }
  return value.dateSingle
    ? `${field} >= '${value.dateSingle}' AND ${field} < '${nextDay(value.dateSingle)}'`
    : '';
}

/**
 * Every active selector aimed at `dataSourceId`, ANDed into one clause.
 *
 * Called once per data source (not per widget) by whoever owns the header's
 * selector state — see `DashboardViewer`/`DashboardPreview`'s `selectorFilters`
 * — so a widget only ever has to look its own `dataSourceId` up in a plain
 * `Record<string, string>` rather than re-walk every selector itself.
 */
export function selectorsWhereForSource(
  dataSourceId: string | undefined,
  selectors: DashboardSelector[],
  state: SelectorState,
): string {
  if (!dataSourceId) return '';
  const clauses = selectors
    .filter((selector) => selector.dataSourceId === dataSourceId)
    .map((selector) => whereForSelector(selector, state[selector.id]))
    .filter(Boolean);
  if (clauses.length === 0) return '';
  return clauses.length === 1 ? clauses[0] : clauses.map((clause) => `(${clause})`).join(' AND ');
}

/** A `dataSourceId → where` map for every data source at least one selector targets. */
export function selectorFiltersFor(
  dataSourceIds: string[],
  selectors: DashboardSelector[],
  state: SelectorState,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const id of dataSourceIds) {
    const where = selectorsWhereForSource(id, selectors, state);
    if (where) map[id] = where;
  }
  return map;
}
